'use client'

import type React from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useSyncExternalStore
} from 'react'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import type { GrammarScriptText } from '@/content/grammar/schema'

type GrammarScript = 'simplified' | 'traditional'

type GrammarScriptContextValue = {
  script: GrammarScript
  setScript: (script: GrammarScript) => void
  toggleScript: () => void
}

const STORAGE_KEY = 'grammar-script-preference'
const STORAGE_EVENT = 'grammar-script-preference-change'

let memoryScriptPreference: GrammarScript = 'simplified'

const GrammarScriptContext = createContext<GrammarScriptContextValue>({
  script: 'simplified',
  setScript: () => {},
  toggleScript: () => {}
})

function loadScriptPreference(): GrammarScript {
  if (typeof window === 'undefined') return 'simplified'
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'simplified' || stored === 'traditional') return stored
  } catch (error) {
    console.error('Failed to load grammar script preference:', error)
  }
  return memoryScriptPreference
}

function saveScriptPreference(script: GrammarScript) {
  memoryScriptPreference = script
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(STORAGE_KEY, script)
  } catch (error) {
    console.error('Failed to save grammar script preference:', error)
  }
  window.dispatchEvent(new Event(STORAGE_EVENT))
}

function subscribeScriptPreference(onStoreChange: () => void) {
  if (typeof window === 'undefined') return () => {}

  const handleStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY) onStoreChange()
  }

  window.addEventListener('storage', handleStorage)
  window.addEventListener(STORAGE_EVENT, onStoreChange)

  return () => {
    window.removeEventListener('storage', handleStorage)
    window.removeEventListener(STORAGE_EVENT, onStoreChange)
  }
}

export function GrammarScriptProvider({
  children
}: {
  children: React.ReactNode
}) {
  const script: GrammarScript = useSyncExternalStore(
    subscribeScriptPreference,
    loadScriptPreference,
    () => 'simplified' as GrammarScript
  )

  const setScript = useCallback((nextScript: GrammarScript) => {
    saveScriptPreference(nextScript)
  }, [])

  const toggleScript = useCallback(() => {
    setScript(script === 'simplified' ? 'traditional' : 'simplified')
  }, [script, setScript])

  return (
    <GrammarScriptContext.Provider value={{ script, setScript, toggleScript }}>
      {children}
    </GrammarScriptContext.Provider>
  )
}

export function useGrammarScript() {
  return useContext(GrammarScriptContext)
}

export function getGrammarScriptText(
  text: string | GrammarScriptText,
  script: GrammarScript
) {
  if (typeof text === 'string') return text
  return script === 'traditional' ? text.t : text.s
}

export function GrammarText({
  text,
  className
}: {
  text: string | GrammarScriptText
  className?: string
}) {
  const { script } = useGrammarScript()
  return <span className={className}>{getGrammarScriptText(text, script)}</span>
}

export function GrammarHanzi({
  s,
  t,
  className
}: GrammarScriptText & {
  className?: string
}) {
  return <GrammarText text={{ s, t }} className={className} />
}

export function GrammarScriptToggle({ className }: { className?: string }) {
  const { script, toggleScript } = useGrammarScript()

  return (
    <label
      className={cn(
        'border-border bg-background text-muted-foreground inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm',
        className
      )}
    >
      <span>简体</span>
      <Switch
        checked={script === 'traditional'}
        onCheckedChange={toggleScript}
        aria-label="Toggle grammar examples between simplified and traditional Chinese"
      />
      <span>繁體</span>
    </label>
  )
}
